import { Drawer, FormControl, FormControlLabel, FormGroup, Grid, InputAdornment, InputLabel, MenuItem, Select, Switch } from "@mui/material";
import { useEffect, useState } from "react";
import { SelectValidator, ValidatorForm } from "react-material-ui-form-validator";
import { LicenseDurationValidityEnum } from "../../_common/enum/licenseDurationValidity.enum";
import { GenericAutoComplete } from "../../_components/autocomplete/generic.autocomplete";
import { PrimaryButton } from "../../_components/button/primary.button";
import { DrawerHeader } from "../../_components/drawer/header.drawer";
import { TextValidatorHcs } from "../../_components/textValidator/textValidatorHcs";
import { toastr } from "../../_components/toastr/toastr";
import { handleErrorResponse } from "../../_helpers/handleResponse.helper";
import { racingLicenseGradeService } from "../../_services/racingLicenseGrade.service";
import { racingLicenseTypeService } from "../../_services/racingLicenseType.service";
import { useDrawerStyles } from "../../_styles/drawer.styles";
import { dropdownService } from "../../_services/dropdown.service";
import { RacingLicenseCategoryEnum } from "../../_common/enum/racingLicenseCategory.enum";

export const RacingLicenseManageDrawer = (params: { isOpen: boolean, itemId: number | null, onSave(): void, onClose(): void }) => {

    const resetData = () => ({
        id: 0,
        racingLicenseTypeId: 0,
        racingLicenseCategoryId: "",
        name: 0,
        price: 0.00,
        firstTimePrice: "0",
        durationValidityId: "",
        isMinor: false,
        rank: ""
    });

    const [data, setData] = useState<any>(resetData);

    useEffect(() => {
        if (!!params.itemId)
            fetchData();
        else
            setData(resetData);
    }, [params.itemId]);

    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    function fetchData() {
        if (!params.itemId) return;

        setIsLoading(true)
        return racingLicenseGradeService
            .get(params.itemId)
            .then(
                (json) => {
                    if (json) {
                        json.firstTimePrice = json.firstTimePrice?.toString() || "0"
                    }
                    setData(json);
                }, handleErrorResponse
            )
            .finally(() => {
                setIsLoading(false);
            });
    }

    function submit(e: any) {
        e.preventDefault();
        setIsSaving(true);
        console.log(data);
        return racingLicenseGradeService
            .submit(data)
            .then(
                () => {
                    toastr.success("Saved successfully");
                    params.onSave();
                },
                (e) => toastr.error(e)
            )
            .finally(() => setIsSaving(false));
    }

    function disable() {
        if (!params.itemId) return;
        setIsLoading(true);
        return racingLicenseGradeService
            .disable(params.itemId)
            .then(
                (d) => {
                    toastr.success("Disabled successfully");
                    fetchData();
                    params.onSave();
                },
                (e) => {
                    toastr.error(e);
                }
            )
            .finally(() => setIsLoading(false));
    }

    function handleChange(event: any) {
        setData((_data: any) => ({ ..._data, [event.target.name]: event.target.value }))
    };


    return (

        <Drawer
            anchor={"right"}
            variant="persistent"
            open={params.isOpen}
            ModalProps={{
                keepMounted: true
            }}
        >

            <div
                tabIndex={0}
                role="button">

                <div>

                    <DrawerHeader name={(!!params.itemId) ? "Update Motorsport Licence" : "Create Motorsport Licence"} onClose={params.onClose} />

                    <ValidatorForm
                        onSubmit={submit}
                        noValidate>

                        <Grid container spacing={1} sx={useDrawerStyles.container}>

                            <Grid item xs={12}>
                                <TextValidatorHcs
                                    key="name"
                                    name="name"
                                    label="Name"
                                    required
                                    fullWidth
                                    validators={['required', 'minStringLength:1']}
                                    errorMessages={['required field', "Field cannot be empty"]}
                                    value={data.name}
                                    onChange={handleChange}
                                    autoFocus
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <GenericAutoComplete
                                    key="racingLicenseTypeId"
                                    label="Select Motorsport Licence"
                                    hasParent={false}
                                    itemId={data.racingLicenseTypeId}
                                    name={"racingLicenseTypeId"}
                                    onItemChange={handleChange}
                                    isRequired={true}
                                    itemsLoader={racingLicenseTypeService.dropdown}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth>
                                    <SelectValidator
                                        label="Licence Category"
                                        name="racingLicenseCategoryId"
                                        value={data.racingLicenseCategoryId}
                                        placeholder="Licence Category"
                                        validators={['required']}
                                        fullWidth
                                        errorMessages={['required field']}
                                        onChange={handleChange}
                                        required
                                        InputLabelProps={{
                                            shrink: !!data.racingLicenseCategoryId,
                                        }}
                                        variant="outlined">
                                        <MenuItem value={RacingLicenseCategoryEnum.National.toString()}>{RacingLicenseCategoryEnum[RacingLicenseCategoryEnum.National]}</MenuItem>
                                        <MenuItem value={RacingLicenseCategoryEnum.International.toString()}>{RacingLicenseCategoryEnum[RacingLicenseCategoryEnum.International]}</MenuItem>
                                    </SelectValidator>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <TextValidatorHcs
                                    type="number"
                                    key="price"
                                    name="price"
                                    label="Price"
                                    required
                                    fullWidth
                                    validators={['required']}
                                    errorMessages={['required field']}
                                    InputProps={{
                                        startAdornment: data.price ? <InputAdornment position="start">{"\u20AC"}</InputAdornment> : null,
                                    }}
                                    value={data.price}
                                    onChange={handleChange}
                                    autoFocus
                                    InputLabelProps={{
                                        shrink: !data.price,
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <TextValidatorHcs
                                    type="number"
                                    key="firstTimePrice"
                                    name="firstTimePrice"
                                    label="First Time Price"
                                    fullWidth
                                    required
                                    validators={['required']}
                                    errorMessages={['required field']}
                                    InputProps={{
                                        startAdornment: data.firstTimePrice && Number(data.firstTimePrice) >= 0 ? <InputAdornment position="start">{"\u20AC"}</InputAdornment> : null,
                                    }}
                                    value={data.firstTimePrice}
                                    onChange={handleChange}
                                    autoFocus
                                    InputLabelProps={{
                                        shrink: !data.firstTimePrice,
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12}>
                                <FormControl fullWidth required>
                                    <SelectValidator
                                        label="Duration Validity"
                                        name="durationValidityId"
                                        value={data.durationValidityId}
                                        placeholder="Duration Validity"
                                        validators={['required']}
                                        fullWidth
                                        errorMessages={['required field']}
                                        required
                                        onChange={handleChange}
                                        InputLabelProps={{
                                            shrink: !!data.durationValidityId,
                                        }}
                                        variant="outlined">
                                        <MenuItem value={""}>Select</MenuItem>
                                        <MenuItem value={LicenseDurationValidityEnum.FiscalYear}>Fiscal Year</MenuItem>
                                        <MenuItem value={LicenseDurationValidityEnum.CalendarYear}>Calendar Year</MenuItem>
                                    </SelectValidator>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12}>
                                <FormGroup>
                                    <FormControlLabel control={<Switch checked={data.isMinor} value={data.isMinor} onChange={() => { setData({ ...data, isMinor: !data.isMinor }) }} />} label="Is Minor" />
                                </FormGroup>
                            </Grid>

                            <Grid item xs={12}>
                                <TextValidatorHcs
                                    key="rank"
                                    name="rank"
                                    label="Rank"
                                    type="number"
                                    helperText="Leave empty if doesn't adhere to ranking process. Else lower number is higher grade"
                                    fullWidth
                                    value={data.rank}
                                    onChange={handleChange}
                                    autoFocus
                                />
                            </Grid>

                        </Grid>

                        <Grid container spacing={1} sx={useDrawerStyles.container}>

                            <Grid item xs={(data.id === 0 ? 12 : 6)}>
                                <PrimaryButton
                                    isSaving={isSaving}
                                    isLoading={isLoading}
                                    type="submit"
                                    variant="save">{"Save"}
                                </PrimaryButton>
                            </Grid>

                            {data.id > 0 &&
                                <Grid item xs={6}>
                                    <PrimaryButton
                                        type="button"
                                        variant="danger"
                                        disabled={isLoading}
                                        onClick={disable}
                                        icon="block"
                                    >
                                        Disable
                                    </PrimaryButton>
                                </Grid>
                            }

                        </Grid>

                    </ValidatorForm>
                </div>
            </div>
        </Drawer>
    );
};